/**
 * Iter59 — ContratosTab extraído de ClienteDetalle.jsx.
 *
 * Lista de contratos de gestora (solo se renderiza si entidad.tipo === 'gestora').
 * Sin estado propio.
 */
import React, { useState, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Plus, Pencil, Trash2, Clock, History, MapPinned, CalendarClock,
} from 'lucide-react';
import { ESTADO_CONTRATO_STYLE } from './_shared';

export const ContratosTab = ({ contratos, entidad, onNuevo, onEditar, onBorrar }) => {
  // Iter61 — filtro por division (solo si la entidad tiene division definida)
  const [filtroDivision, setFiltroDivision] = useState('');
  const divisiones = useMemo(() => {
    const set = new Set();
    contratos.forEach((c) => { if (c.division) set.add(c.division); });
    return [...set].sort();
  }, [contratos]);
  const contratosFiltrados = filtroDivision
    ? contratos.filter((c) => c.division === filtroDivision)
    : contratos;

  return (
  <div data-testid="tab-contratos-content">
    <div className="flex items-center justify-between mb-4">
      <p className="text-sm text-ink-500">Contratos que esta gestora opera. Cada contrato agrupa los municipios servidos.</p>
      <Button className="bg-brand-500 hover:bg-brand-600 text-white" onClick={onNuevo} data-testid="btn-nuevo-contrato">
        <Plus className="w-4 h-4 mr-1.5" /> Nuevo contrato
      </Button>
    </div>

    {(entidad?.division || divisiones.length > 0) && (
      <div className="flex items-center gap-2 mb-3 text-xs" data-testid="filtro-division-wrap">
        <span className="text-ink-500 font-medium">Filtrar por división:</span>
        <button
          type="button"
          onClick={() => setFiltroDivision('')}
          className={`px-2 py-1 rounded-full border transition ${
            !filtroDivision ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-ink-600 border-ink-200 hover:bg-ink-50'
          }`}
          data-testid="filtro-division-all"
        >Todas</button>
        {divisiones.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setFiltroDivision(d)}
            className={`px-2 py-1 rounded-full border transition ${
              filtroDivision === d ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-ink-600 border-ink-200 hover:bg-ink-50'
            }`}
            data-testid={`filtro-division-${d}`}
          >{d}</button>
        ))}
      </div>
    )}
    {contratosFiltrados.length === 0 ? (
      <Card className="p-10 text-center text-ink-400 bg-white border-ink-200">
        {contratos.length === 0 ? 'Aún no hay contratos para esta gestora.' : 'Sin contratos para esta división.'}
      </Card>
    ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {contratosFiltrados.map((c) => (
          <Card key={c.id} className="p-5 border-ink-200 bg-white card-hover" data-testid={`contrato-${c.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-heading font-semibold text-ink-900 text-base">{c.nombre}</h4>
                  <Badge className={`${ESTADO_CONTRATO_STYLE[c.estado] || ''} border`}>{c.estado}</Badge>
                  {c.division && (
                    <Badge className="bg-brand-50 text-brand-700 border border-brand-200" data-testid={`contrato-div-${c.id}`}>
                      {c.division}
                    </Badge>
                  )}
                </div>
                {c.codigo_interno && <p className="text-[11px] font-mono text-ink-400 mt-0.5">{c.codigo_interno}</p>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => onEditar(c)} className="p-1.5 rounded hover:bg-ink-100 text-ink-600" title="Editar" data-testid={`btn-edit-contrato-${c.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => onBorrar(c)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Eliminar">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-ink-500 mb-2">
              {c.fecha_inicio && <span><Clock className="w-3 h-3 inline mr-0.5" />Desde {c.fecha_inicio}</span>}
              {c.fecha_fin && <span>· Hasta {c.fecha_fin}</span>}
              {c.proxima_licitacion_fecha && <span className="text-amber-700">· Licita {c.proxima_licitacion_fecha}</span>}
            </div>
            {c.municipios_detalle?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {c.municipios_detalle.slice(0, 8).map((m) => (
                  <span key={m.codigo_ine} className="inline-flex items-center gap-1 text-[11px] bg-ink-50 border border-ink-200 text-ink-700 rounded px-1.5 py-0.5">
                    <MapPinned className="w-3 h-3 text-ink-400" /> {m.nombre}
                  </span>
                ))}
                {c.municipios_detalle.length > 8 && (
                  <span className="text-[11px] text-ink-500">+{c.municipios_detalle.length - 8} más</span>
                )}
              </div>
            )}
            {c.historico_gestoras?.length > 0 && (
              <p className="text-[11px] text-ink-400 mt-2 flex items-center gap-1">
                <History className="w-3 h-3" /> {c.historico_gestoras.length} gestora{c.historico_gestoras.length > 1 ? 's' : ''} anterior{c.historico_gestoras.length > 1 ? 'es' : ''}
              </p>
            )}
            {c.num_avisos_pendientes > 0 && (
              <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> {c.num_avisos_pendientes} aviso{c.num_avisos_pendientes > 1 ? 's' : ''} pendiente{c.num_avisos_pendientes > 1 ? 's' : ''}
              </p>
            )}
          </Card>
        ))}
      </div>
    )}
  </div>
  );
};

export default ContratosTab;
